import { useState } from 'react'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

import {
  Member,
  Post,
  PostReactionParticipant,
} from '@tribeplatform/gql-client/types'
import { useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { useCMS } from '../CMS/hooks/useCMS.js'
import { sanitizePostType } from '../CMS/utils/utils.js'
import { useStaticIntl, T, useI18n } from '../i18n/index.js'
import { I18nShape } from '../i18n/types.js'
import { ReactionRenderer } from './ReactionRenderer.js'
import { ReactionsModal } from './ReactionsModal.js'

const MAX_REACTION_NAMES = 10

export const getListFromParticipants =
  (intl: I18nShape) =>
  (
    nodes: PostReactionParticipant[],
    totalCount: number,
    authUser?: Member,
  ): string => {
    // TODO this fn needs refactor / optimization
    const { $t } = intl
    if (typeof nodes !== 'object') {
      return ''
    }
    const moreCount = totalCount - MAX_REACTION_NAMES
    const namesList = nodes.slice(0, MAX_REACTION_NAMES).map(node => {
      const { participant } = node
      return authUser?.id === participant?.id
        ? $t({
            id: 'Generics.you',
            defaultMessage: 'you',
          })
        : participant?.name
    })

    if (moreCount > 0) {
      return $t(
        {
          id: 'Post.PostInfoBar.ReactionParticipantsLongText',
          defaultMessage: '{name_list} and {more_count} more',
        },
        {
          name_list: intl.formatList(namesList, {
            type: 'conjunction',
            style: 'short',
          }),
          more_count: moreCount,
        },
      )
    }
    return intl.formatList(namesList, {
      type: 'conjunction',
      style: 'long',
    })
  }

interface Props {
  post: Post
  showReactionCount?: boolean
  showReplyCount?: boolean
  showFollowCount?: boolean
  className?: string
}
export const PostInfoBar = ({
  post,
  showReactionCount = true,
  showReplyCount = true,
  showFollowCount = true,
  className,
}: Props) => {
  const { $t } = useI18n()
  const postTypeId = post?.postTypeId
  const participantsSummary = useStaticIntl(getListFromParticipants)
  const { postTypes } = useCMS()
  const maybePostType = postTypes?.find(({ id }) => id === postTypeId)
  const sanitizedPostType = sanitizePostType(maybePostType)
  const customReactions = sanitizedPostType?.customReactions
  const { reactions, totalRepliesCount, followersCount } = post
  const { data: authMember } = useAuthMember()

  const [reactionsModal, setReactionsModal] = useState(false)
  const showFollowersCount =
    typeof followersCount === 'number' && followersCount > 1

  if (!totalRepliesCount && !showFollowersCount && !post?.reactionsCount) {
    return null
  }

  if (!postTypeId) {
    return null
  }

  return (
    <>
      <div
        className={twMerge(clsx('flex items-center h-7 space-s-3', className))}
      >
        {showReactionCount && !!post?.reactionsCount && (
          <div className="flex-grow min-w-0">
            <div className="flex items-center max-w-full">
              {reactions?.length > 0 && (
                <>
                  {reactions
                    ?.sort((a, b) => b?.count - a?.count)
                    ?.map(reaction => {
                      return (
                        <Tooltip key={reaction.reaction}>
                          <Tooltip.Trigger
                            onClick={() => setReactionsModal(true)}
                          >
                            <ReactionRenderer
                              reaction={reaction}
                              customReactions={customReactions}
                              label={$t({
                                defaultMessage: 'Reactions',
                                id: 'Generics.Reactions',
                              })}
                            />
                          </Tooltip.Trigger>
                          {typeof reaction.participants?.nodes === 'object' && (
                            <Tooltip.Panel>
                              {participantsSummary(
                                reaction.participants?.nodes,
                                reaction.count,
                                authMember,
                              )}
                            </Tooltip.Panel>
                          )}
                        </Tooltip>
                      )
                    })}
                  <div className="mx-3 text-content-subdued text-sm">
                    {post?.reactionsCount}
                  </div>
                </>
              )}
              {reactions?.length === 0 && <div>&nbsp;</div>}
              {reactions?.length === undefined && (
                <div className="flex items-center animate-pulse">
                  <button
                    type="button"
                    className="rounded-full p-1 bg-skeleton w-9"
                  >
                    &nbsp;
                  </button>
                  <div className="mx-3 text-content-subdued text-sm">
                    {post?.reactionsCount}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {showFollowersCount && showFollowCount ? (
          <div className="text-content-subdued hidden sm:block shrink-0 whitespace-nowrap text-sm">
            <T
              defaultMessage="{followers_count, plural, =1 {# follower} other {# followers}}"
              id="Follower.Count"
              values={{
                followers_count: followersCount - 1,
              }}
            />
          </div>
        ) : null}
        {totalRepliesCount && showReplyCount ? (
          <div className="text-content-subdued shrink-0 whitespace-nowrap text-sm">
            <T
              defaultMessage="{replies_count, plural, =1 {# reply} other {# replies}}"
              id="Reply.Count"
              values={{
                replies_count: totalRepliesCount,
              }}
            />
          </div>
        ) : null}
      </div>
      {reactionsModal && (
        <ReactionsModal
          open={reactionsModal}
          onClose={() => setReactionsModal(false)}
          reactions={reactions}
          postId={post?.id}
        />
      )}
    </>
  )
}
